/**
 * 圖示、塗鴉、文字、QR Code 的縮放限制（SSOT）。
 * 下限為固定常數；上限依「輸出畫布（輸入圖）像素」動態計算，僅設絕對上限以防異常大圖。
 */

/** 圖示／塗鴉／QR Code 尺寸（px）：下限 */
export const ICON_QR_SIZE_MIN = 16;

/** 文字浮水印字級（px）：下限 */
export const WATERMARK_FONT_SIZE_MIN = 8;

/** 絕對上限：圖示／塗鴉／QR 不超過此值（避免極大畫布時滑桿失控） */
export const ICON_QR_SIZE_ABS_MAX = 2000;

/** 絕對上限：浮水印字級不超過此值 */
export const WATERMARK_FONT_SIZE_ABS_MAX = 500;

/**
 * 依畫布尺寸算出圖示／塗鴉／QR 的尺寸上限（長邊，且不超過絕對上限）。
 */
export function getIconQrSizeMax(
  canvasWidth: number,
  canvasHeight: number
): number {
  if (!canvasWidth || !canvasHeight) return ICON_QR_SIZE_ABS_MAX;
  const longSide = Math.max(canvasWidth, canvasHeight);
  return Math.min(ICON_QR_SIZE_ABS_MAX, Math.round(longSide));
}

/**
 * 依畫布尺寸算出浮水印字級上限（長邊，且不超過絕對上限）。
 */
export function getWatermarkFontSizeMax(
  canvasWidth: number,
  canvasHeight: number
): number {
  if (!canvasWidth || !canvasHeight) return WATERMARK_FONT_SIZE_ABS_MAX;
  const longSide = Math.max(canvasWidth, canvasHeight);
  return Math.min(WATERMARK_FONT_SIZE_ABS_MAX, Math.round(longSide));
}

/**
 * 將圖示／塗鴉／QR 尺寸限制在 [ICON_QR_SIZE_MIN, 畫布長邊]（繪製時呼叫，依當時畫布尺寸）。
 */
export function clampIconQrSizeToCanvas(
  size: number,
  canvasWidth: number,
  canvasHeight: number
): number {
  const maxSize = getIconQrSizeMax(canvasWidth, canvasHeight);
  return Math.max(ICON_QR_SIZE_MIN, Math.min(maxSize, size));
}

/**
 * 將浮水印字級限制在 [WATERMARK_FONT_SIZE_MIN, 畫布長邊]（繪製時呼叫）。
 */
export function clampWatermarkFontSizeToCanvas(
  fontSize: number,
  canvasWidth: number,
  canvasHeight: number
): number {
  const maxFontSize = getWatermarkFontSizeMax(canvasWidth, canvasHeight);
  return Math.max(WATERMARK_FONT_SIZE_MIN, Math.min(maxFontSize, fontSize));
}

/** 僅依下限 clamp（傳參時用；上限由 pipeline 依畫布再 clamp） */
export function clampIconQrSizeMin(size: number): number {
  return Math.max(ICON_QR_SIZE_MIN, size);
}

/** 僅依下限 clamp（傳參時用） */
export function clampWatermarkFontSizeMin(fontSize: number): number {
  return Math.max(WATERMARK_FONT_SIZE_MIN, fontSize);
}
